import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Tooltip } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import TextButton from 'Components/ComponentLibrary/Components/Uncategorized/TextButton';
import JustificationInput
  from 'Views/Page/Content/Templates/WhatIfTemplate/Components/LinterRuleResultsDrawer/LinterRuleResults/JustificationInput';
import { useFilterContext } from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/FilterContext';
import {
  useTabContentContext,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Context';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import fr from 'facet-rest';
import useQueryParams from 'Hooks/useQueryParams';
import { FACET_PLAN_SIDEBAR_QUERY_PARAM } from 'Views/Page/Content/Templates/WhatIfTemplate/Components/Sidebar/consts';
import {
  FDBodyText, FDIcon, FDLabelText, FDPrimaryActionButton, FDStack,
} from '@facet-wealth/design';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import { Text } from 'Packages/FacetDesign/Components/Text';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { fullDateAtTime } from 'Consts/DateFormats';
import moment from 'moment';

const styles = (theme) => ({
  ruleResult: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    gap: theme.spacing(1),
  },
  tooltip: {
    alignSelf: 'flex-start',
  },
  viewButton: {
    fontSize: 12,
    padding: 0,
  },
  helpIcon: {
    fontSize: theme.typography.fontSize1Pt25Rem,
    paddingRight: theme.spacing(0.5),
  },
  ruleJustification: {
    paddingLeft: theme.spacing(2),
  },
  ruleDetails: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  ruleTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
});

const ruleTypeToTabType = {
  accounts: 'accountDetails',
  allocation: 'allocationDetails',
  scenarioDetails: 'scenarioDetails',
  nextUpReview: 'nextUpDetails',
  taxStrategy: 'taxStrategy',
};

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('ruleText', {
    _component: () => Text,
    _customProps: { Component: FDBodyText, size: 'sm' },
  }, overrideReadOnly)
  .nameOverride('ruleRichtext', {
    _component: () => Richtext,
    _customProps: { Component: FDBodyText, size: 'sm' },
  }, overrideReadOnly);

const RuleDetail = ({
  className, classes, ruleResultResource, handleCloseDialog,
}) => {
  const [currentResource, setCurrentResource] = useState(ruleResultResource);
  const [saving, setSaving] = React.useState(false);
  const { chartTimeframe, setChartTimeframe } = useFilterContext();
  const {
    activeTabResourceType,
    setActiveTabResourceType,
  } = useTabContentContext();
  const { loadModalCollectionByLink } = useModalContext();
  const [queryParams, setQuery] = useQueryParams();
  const currentTab = queryParams.get(FACET_PLAN_SIDEBAR_QUERY_PARAM);
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();

  const handleViewOnClick = (ruleResource) => {
    const ruleType = ruleResource.attr('ruleType');
    const tabType = ruleTypeToTabType[ruleType];
    switch (ruleType) {
      case 'accounts':
      case 'allocation':
      case 'scenarioDetails':
        if (chartTimeframe !== ruleResource.attr('allocationYear')) {
          setChartTimeframe(ruleResource.attr('allocationYear'));
        }
        if (activeTabResourceType !== tabType) { // change to tab if not selected
          setActiveTabResourceType(tabType);
        }
        break;
      case 'taxStrategy':
      case 'nextUpReview':
        if (activeTabResourceType !== tabType) { // change to tab if not selected
          setActiveTabResourceType(tabType);
        }
        break;
      case 'sidebar':
        if (currentTab !== ruleResource.attr('sidebarTab')) {
          queryParams.set(FACET_PLAN_SIDEBAR_QUERY_PARAM, ruleResource.attr('sidebarTab'));
          setQuery(queryParams);
        }
        break;
      default:
        loadModalCollectionByLink(
          ruleResource
            .relationship(fr.relationships.modalSelfLink.name)
            .link('related')
            .url(),
        );
    }

    handleCloseDialog(false);
  };
  const handleMarkReviewedOnClick = async (ruleResource) => {
    setSaving(true);
    try {
      const [updatedResource] = await ruleResource.relationship(fr.relationships.markReviewed.name).link('related').post({}, { deprecatedAllowAlterOfRelation: true });
      // This is bad, but unless we want to refactor the linter backend to return the rule text and description
      // from the create usecases, this is the fastest workaround I see
      currentResource.attr('reviewedAt', updatedResource.attr('reviewedAt'));
      setCurrentResource(currentResource);
      displaySuccess('Validation rule approved');
    } catch (err) {
      displayErrorMessage(err.message || 'Error approving validation rule');
    } finally {
      setSaving(false);
    }
  };

  const showViewButton = currentResource.attr('ruleType') !== undefined;
  const showApproveButton = !!currentResource.relationship(fr.relationships.markReviewed.name) && !currentResource.attr('reviewedAt');
  return (
    <div className={classNames(classes.ruleResult, className)} test={`scenario-linter-rule-detail-${currentResource.attr('ruleDescription')}`}>
      <div className={classes.ruleDetails}>
        <div className={classes.ruleTextContainer}>
          <Tooltip title={currentResource.attr('ruleDescription')} placement={'top'} arrow className={classes.tooltip}>
            <HelpIcon color="primary" fontSize="default" className={classes.helpIcon} />
          </Tooltip>
          <FDStack direction="column" spacing={1} flex={1}>
            <MetaControl resource={currentResource} attrName="ruleText" metaOverrideSet={metaOverrideSet} />
            <MetaControl resource={currentResource} attrName="ruleRichtext" metaOverrideSet={metaOverrideSet} />
          </FDStack>
        </div>
        {showViewButton && (
          <div>
            <TextButton
              onClick={() => handleViewOnClick(currentResource)}
              variant={'underlined'}
              weight="light"
              size={'small'}
              className={classes.viewButton}
            >
              {'View'}
            </TextButton>
          </div>
        )}
      </div>
      {showApproveButton && (
        <FDStack direction="row" spacing={1} justifyContent={'flex-end'}>
          <FDPrimaryActionButton
            loading={saving}
            onClick={() => handleMarkReviewedOnClick(currentResource)}
            label={'Approve'}
            size={'sm'}
          />
        </FDStack>
      )}
      {!!currentResource.attr('reviewedAt') && (
        <FDStack direction="row" spacing={1} justifyContent={'flex-end'}>
          <FDIcon icon={'checkCircleIcon'} color={'success'} size={'sm'} />
          <FDLabelText color={'textSecondary'} size={'md'} value={`Reviewed on ${moment(currentResource.attr('reviewedAt')).format(fullDateAtTime)}`} />
        </FDStack>
      )}
      {currentResource.attr('ruleLevel') === 'warning' && (
      <div className={classes.ruleJustification}>
        <JustificationInput ruleResultResource={currentResource} />
      </div>
      )}
    </div>
  );
};

RuleDetail.defaultProps = {
  className: '',
};

RuleDetail.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  ruleResultResource: PropTypes.object.isRequired,
  handleCloseDialog: PropTypes.func.isRequired,
};

export default withStyles(styles)(RuleDetail);
